import {
  Button,
  Card,
  CardBody,
  Select,
  SelectItem,
  Switch,
} from '@nextui-org/react';
import { useTranslation } from 'common/src/hooks/useTranslation';
import {
  growerKey,
  parseGrowerExceptions,
  type GrowerException,
  type GrowerTariffMode,
} from 'common/src/utils/presettlement-grower-exceptions';

const inputClass =
  'h-11 w-full rounded-xl border border-divider bg-background px-3 text-sm outline-none focus:border-primary';

export default function GrowerExceptionsEditor({
  growers,
  value,
  onChange,
  defaultCommission,
  disabled = false,
}: {
  growers: string[];
  value: GrowerException[];
  onChange: (value: GrowerException[]) => void;
  defaultCommission: number;
  disabled?: boolean;
}) {
  const { t } = useTranslation('preSettlements');
  const update = (index: number, change: Partial<GrowerException>) =>
    onChange(
      value.map((rule, i) => (i === index ? { ...rule, ...change } : rule))
    );
  // Retain saved growers even when they have no shipments in the current data.
  const options = [
    ...new Set([...growers, ...value.flatMap((rule) => rule.growers)]),
  ]
    .filter((name) => growerKey(name))
    .sort((a, b) => a.localeCompare(b));
  const modes: { key: GrowerTariffMode | 'default'; label: string }[] = [
    { key: 'default', label: t('Usar tarifa calculada') },
    { key: 'replace', label: t('Tarifa de reemplazo') },
    { key: 'percentage-discount', label: t('Descuento porcentual') },
    { key: 'amount-discount', label: t('Descuento fijo por kg') },
  ];
  return (
    <Card shadow='sm'>
      <CardBody className='p-6'>
        <div className='flex flex-wrap items-center justify-between gap-3'>
          <h3 className='text-lg font-semibold'>
            {t('Excepciones por productor')}
          </h3>
          <Button
            size='sm'
            variant='flat'
            isDisabled={disabled}
            onPress={() =>
              onChange([
                ...value,
                {
                  growers: [],
                  commissionPercentage: defaultCommission,
                  tariff: null,
                },
              ])
            }
          >
            {t('Agregar excepción')}
          </Button>
        </div>
        <p className='mt-2 text-sm text-default-500'>
          {t(
            'Selecciona uno o más productores por excepción. Los demás mantienen la comisión y tarifa generales.'
          )}
        </p>
        <p className='mt-1 text-xs text-default-500'>
          {t(
            'Los descuentos se aplican después de calcular la tarifa, sin modificar el promedio de los demás productores. La tarifa mínima es 0 USD/kg.'
          )}
        </p>
        {!value.length && (
          <p className='mt-5 text-sm text-default-400'>
            {t('Sin excepciones configuradas.')}
          </p>
        )}
        <fieldset disabled={disabled} className='mt-5 space-y-4'>
          {value.map((rule, index) => {
            const taken = new Set(
              value
                .filter((_, i) => i !== index)
                .flatMap((other) => other.growers.map(growerKey))
            );
            const available = options.filter(
              (name) => !taken.has(growerKey(name))
            );
            const invalid = !parseGrowerExceptions([rule]);
            const percentage = rule.tariff?.mode === 'percentage-discount';
            return (
              <div key={index} className='rounded-xl border border-divider p-4'>
                <div className='mb-4 flex items-center justify-between gap-3'>
                  <span className='text-sm font-semibold'>
                    {t('Excepción')} {index + 1}
                  </span>
                  <Button
                    size='sm'
                    color='danger'
                    variant='light'
                    isDisabled={disabled}
                    aria-label={`${t('Eliminar excepción')} ${index + 1}`}
                    onPress={() => onChange(value.filter((_, i) => i !== index))}
                  >
                    {t('Eliminar excepción')}
                  </Button>
                </div>
                <Select
                  label={t('Productores')}
                  selectionMode='multiple'
                  variant='bordered'
                  placeholder={t('Seleccionar productores')}
                  isDisabled={disabled}
                  selectedKeys={new Set(rule.growers)}
                  onSelectionChange={(keys) =>
                    update(index, {
                      growers:
                        keys === 'all' ? available : Array.from(keys, String),
                    })
                  }
                >
                  {available.map((name) => (
                    <SelectItem key={name} textValue={name}>
                      {name}
                    </SelectItem>
                  ))}
                </Select>
                <div className='mt-5 grid gap-5 md:grid-cols-2'>
                  <fieldset className='min-w-0 rounded-xl border border-divider p-4'>
                    <legend className='px-2 text-sm font-semibold'>
                      {t('Comisión')}
                    </legend>
                    <Switch
                      size='sm'
                      isDisabled={disabled}
                      isSelected={rule.commissionPercentage !== null}
                      onValueChange={(enabled) =>
                        update(index, {
                          commissionPercentage: enabled
                            ? defaultCommission
                            : null,
                        })
                      }
                    >
                      {t('Comisión especial')}
                    </Switch>
                    {rule.commissionPercentage !== null ? (
                      <label className='mt-2 block text-xs text-default-500'>
                        {t('Comisión exportadora')} (%)
                        <input
                          className={`${inputClass} mt-1`}
                          type='number'
                          min='0'
                          max='100'
                          step='any'
                          value={
                            Number.isFinite(rule.commissionPercentage)
                              ? rule.commissionPercentage
                              : ''
                          }
                          aria-invalid={
                            !Number.isFinite(rule.commissionPercentage) ||
                            rule.commissionPercentage < 0 ||
                            rule.commissionPercentage > 100
                          }
                          onChange={(event) =>
                            update(index, {
                              commissionPercentage:
                                event.target.value === ''
                                  ? NaN
                                  : Number(event.target.value),
                            })
                          }
                        />
                      </label>
                    ) : (
                      <p className='mt-2 text-xs text-default-500'>
                        {t('Comisión general')}: {defaultCommission}%
                      </p>
                    )}
                  </fieldset>
                  <fieldset className='min-w-0 rounded-xl border border-divider p-4'>
                    <legend className='px-2 text-sm font-semibold'>
                      {t('Tarifa')}
                    </legend>
                    <Select
                      label={t('Ajuste de tarifa')}
                      variant='bordered'
                      isDisabled={disabled}
                      disallowEmptySelection
                      selectedKeys={[rule.tariff?.mode ?? 'default']}
                      onSelectionChange={(keys) => {
                        const mode = Array.from(keys)[0] as
                          | GrowerTariffMode
                          | 'default';
                        update(index, {
                          tariff: mode === 'default' ? null : { mode, value: 0 },
                        });
                      }}
                    >
                      {modes.map((mode) => (
                        <SelectItem key={mode.key}>{mode.label}</SelectItem>
                      ))}
                    </Select>
                    {rule.tariff && (
                      <label className='mt-3 block text-xs text-default-500'>
                        {rule.tariff.mode === 'replace'
                          ? t('Tarifa de reemplazo')
                          : t('Descuento')}{' '}
                        ({percentage ? '%' : 'USD/kg'})
                        <input
                          className={`${inputClass} mt-1`}
                          type='number'
                          min='0'
                          max={percentage ? 100 : undefined}
                          step='any'
                          value={
                            Number.isFinite(rule.tariff.value)
                              ? rule.tariff.value
                              : ''
                          }
                          aria-invalid={
                            !Number.isFinite(rule.tariff.value) ||
                            rule.tariff.value < 0 ||
                            (percentage && rule.tariff.value > 100)
                          }
                          onChange={(event) =>
                            update(index, {
                              tariff: {
                                ...rule.tariff!,
                                value:
                                  event.target.value === ''
                                    ? NaN
                                    : Number(event.target.value),
                              },
                            })
                          }
                        />
                      </label>
                    )}
                  </fieldset>
                </div>
                {invalid && (
                  <p role='alert' className='mt-3 text-xs text-danger'>
                    {t(
                      'Selecciona productores y configura al menos una excepción válida. Los porcentajes deben estar entre 0 y 100 y los montos no pueden ser negativos.'
                    )}
                  </p>
                )}
              </div>
            );
          })}
        </fieldset>
      </CardBody>
    </Card>
  );
}
